import { useCallback, useMemo } from 'react';

import { useHideSection } from '../data/useHideSection';
import { useUnhideSection } from '../data/useUnhideSection';
import { findNode } from '../model/templateProjection';
import { failureFor } from '../state/selectors';
import { LABEL_KEYS } from '../viewmodels/messages';
import { runOperation, type RunOperationDeps } from './runOperation';

export type SectionActions = {
  hideSection: (sectionId: string) => Promise<void>;
  unhideSection: (sectionId: string) => Promise<void>;
  retry: (sectionId: string) => Promise<void>;
};

export function useSectionActions(deps: RunOperationDeps): SectionActions {
  const { getLatestState } = deps;
  const hide = useHideSection();
  const unhide = useUnhideSection();

  const hideSection = useCallback(
    async (sectionId: string) => {
      const { templateId, channelId } = getLatestState();

      await runOperation(
        {
          op: {
            kind: 'hideSection',
            targetId: sectionId,
            change: { kind: 'hideSection', sectionId },
          },
          execute: context =>
            hide({ channelId, templateId, sectionId }, context),
          successMessage: { key: LABEL_KEYS.hideSectionSucceededToast },
        },
        deps
      );
    },
    [getLatestState, hide, deps]
  );

  const unhideSection = useCallback(
    async (sectionId: string) => {
      const { templateId, channelId, replica } = getLatestState();
      const node = replica ? findNode(replica, sectionId) : undefined;

      if (node?.type !== 'section') {
        return;
      }

      const { section } = node;

      await runOperation(
        {
          op: {
            kind: 'unhideSection',
            targetId: sectionId,
            change: { kind: 'unhideSection', sectionId },
          },
          execute: context =>
            unhide({ channelId, templateId, section }, context),
          successMessage: { key: LABEL_KEYS.unhideSectionSucceededToast },
        },
        deps
      );
    },
    [getLatestState, unhide, deps]
  );

  const retry = useCallback(
    async (sectionId: string) => {
      const failed = failureFor(getLatestState(), sectionId);
      const kind = failed?.op.kind;

      if (kind === 'hideSection') {
        await hideSection(sectionId);
      }

      if (kind === 'unhideSection') {
        await unhideSection(sectionId);
      }
    },
    [getLatestState, hideSection, unhideSection]
  );

  return useMemo(
    () => ({ hideSection, unhideSection, retry }),
    [hideSection, unhideSection, retry]
  );
}
